import { ref, computed, watch,shallowRef } from 'vue'
import { initializeApp, getApp, deleteApp } from 'firebase/app'
import {
  getFirestore,
  terminate,
  collection as firestoreCollection,
  doc as firestoreDocument,
  getDocFromServer,
  initializeFirestore,
  memoryLocalCache,
  query,
  onSnapshot,
  where,
} from 'firebase/firestore'
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth'
import { useDebounce, toValue } from '@vueuse/core'

import { useFirestore } from './firestore-sdk-wrapper.js'

import { isNil, is } from 'ramda'
import { useLogger } from '@booksy-com/vue-tools'
import { useAuthentication } from '@/composables/authentication'
import { useEppo } from '@/composables/eppo'

import { transformKeysToCamelCase } from '@/lib/helpers'

import { getFirestoreConfigForWeb } from './api'

import {
  createPath,
  hasNullOrUndefined,
} from './utils'

import {
  COLLECTIONS,
} from './collections/consts.ts'
import {
  DOCUMENTS,
} from './documents/consts.ts'

import {
  FEATURE_FLAGS,
} from '@/lib/consts.ts'

let FIRESTORE_DB
let FIRESTORE_APP_NAME
let _lastInitContext
let _reinitializePromise

const isInitialized = ref(false)
const isReinitializing = ref(false)
let _unsubscribeUserPermissonChanges
let _checkFeatureFlagStateInterval

const FF_CHECK_INTERVAL_MS = 60000 // 1m

/** Max automatic listener restarts per subscription (avoids infinite loops on persistent failures). */
const MAX_LISTEN_AUTO_RESTARTS = 3

const LISTEN_AUTO_RESTART_DELAY_MS = 1500

const logger = useLogger('booksy-firestore')

const isFirestoreShuttingDownMessage = (error) => {
  return is(String, error?.message) && error.message.includes('Firestore shutting down')
}

/**
 * Transient errors where a new onSnapshot subscription may recover (WebView / network).
 * Permission / auth failures are intentionally excluded.
 */
const isRetryableFirestoreListenError = (error) => {
  if (!useEppo().ff(FEATURE_FLAGS.Fix_CalendarFirestoreResyncVisibility)) return false

  if (isFirestoreShuttingDownMessage(error)) return false
  const code = error?.code
  if (!is(String, code)) return false
  return (
    code === 'unavailable'
    || code === 'deadline-exceeded'
    || code === 'resource-exhausted'
    || code === 'aborted'
    || code === 'internal'
  )
}

function createListenRetryState () {
  const listenRetryNonce = shallowRef(0)
  const totalAutoRestarts = shallowRef(0)
  const state = { retryTimeoutId: null }

  const clearScheduledRestart = () => {
    if (state.retryTimeoutId != null) {
      clearTimeout(state.retryTimeoutId)
      state.retryTimeoutId = null
    }
  }

  const scheduleRestart = (fn) => {
    clearScheduledRestart()
    state.retryTimeoutId = setTimeout(() => {
      state.retryTimeoutId = null
      fn()
    }, LISTEN_AUTO_RESTART_DELAY_MS)
  }

  return { listenRetryNonce, totalAutoRestarts, clearScheduledRestart, scheduleRestart }
}

export function useBooksyFirestore () {
  const _getConfig = async () => {
    logger.info('configuration fetching...')
    try {
      const response = await getFirestoreConfigForWeb()

      return response?.data
    } catch (e) {
      logger.info('configuration fetching failed')
      logger.error(e.message)
      throw new Error()
    }
  }

  const _initFirestore = async ({ config }) => {
    logger.info('initialize firebase app...')
    try {
      const firestoreApp = initializeApp(transformKeysToCamelCase(config))
      FIRESTORE_APP_NAME = firestoreApp.name
      FIRESTORE_DB = initializeFirestore(firestoreApp, {
        localCache: memoryLocalCache(),
      }, config.database)

      logger.info('firebase app initialized')
    } catch (e) {
      logger.info('firebase application initialization failed')
      logger.error(e.message)
      throw new Error()
    }
  }

  const _authorize = async ({ token }) => {
    logger.info('authorize with token...')
    try {
      const firestoreApp = getApp(FIRESTORE_APP_NAME)
      await signInWithCustomToken(getAuth(firestoreApp), token)
      logger.info('authorized')
    } catch (e) {
      logger.info('firebase authorization failed')
      logger.error(e.message)
      throw new Error()
    }
  }

  const _handleChangingLoggedUserPermissions = (context) => {
    _unsubscribeUserPermissonChanges = onSnapshot(query(firestoreCollection(FIRESTORE_DB,
      createPath(COLLECTIONS.RESOURCES, { businessId: context?.business.id })
    ), where('staff_user_id', '==', context?.user.id)), (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (!['modified', 'removed'].includes(change.type)) return

        const userStafferAccessLevel = change.doc.get('staff_access_level')
        const userStafferDeleted = change.doc.get('deleted')
        if (userStafferAccessLevel !== context?.user.access_level
          || !isNil(userStafferDeleted)
          || change.type === 'removed'
        ) {
          useAuthentication().logout()
        }
      })
    })
  }

  const _startCheckingFeatureFlagStatus = () => {
    _checkFeatureFlagStateInterval = setInterval(() => {
      if (useEppo().ff(FEATURE_FLAGS.Feature_ProviderFirestore)) return

      destroy()
    }, FF_CHECK_INTERVAL_MS)
  }

  const _stopCheckingFeatureFlagStatus = () => {
    if (_checkFeatureFlagStateInterval) {
      clearInterval(_checkFeatureFlagStateInterval)
      _checkFeatureFlagStateInterval = undefined
    }
  }

  const init = async (context) => {
    if (context) {
      _lastInitContext = context
    }

    if (isInitialized.value) {
      logger.info('firestore already initialized... skip it')
      return
    }

    try {
      const config = await _getConfig()
      await _initFirestore(config)
      await _authorize(config)
      _handleChangingLoggedUserPermissions?.(context)
      _startCheckingFeatureFlagStatus()
      isInitialized.value = true
      logger.info('ready')
    } catch (e) {
      logger.error('firestore failed to initialize due to errors')
    }
  }

  const destroy = async () => {
    logger.info('destroying...')

    if (!isInitialized.value) {
      logger.info('not initalized... skip it')
      return
    }

    try {
      _unsubscribeUserPermissonChanges?.()
      _stopCheckingFeatureFlagStatus()
      const firestoreApp = getApp(FIRESTORE_APP_NAME)
      await signOut(getAuth(firestoreApp))
      const firestoreInstance = getFirestore(firestoreApp)
      await terminate(firestoreInstance)
      await deleteApp(firestoreApp)
      isInitialized.value = false
      logger.info('firestore app destroyed')
    } catch (e) {
      logger.error(e.message)
    }
  }

  const probeConnection = ({ timeoutMs = 5000 } = {}) => {
    if (!isInitialized.value) return Promise.resolve(false)

    const businessId = _lastInitContext?.business?.id
    if (!businessId) return Promise.resolve(false)

    const probePath = createPath(DOCUMENTS.BUSINESS, { businessId })

    return new Promise((resolve) => {
      let isSettled = false
      const timeoutId = setTimeout(() => {
        isSettled = true
        logger.info('firestore probe timed out')
        resolve(false)
      }, timeoutMs)

      getDocFromServer(firestoreDocument(FIRESTORE_DB, probePath))
        .then(() => {
          if (isSettled) return

          isSettled = true
          clearTimeout(timeoutId)
          logger.info('firestore probe successful')
          resolve(true)
        })
        .catch((error) => {
          if (isSettled) return

          isSettled = true
          clearTimeout(timeoutId)
          logger.info('firestore probe failed')
          _defaultErrorHandler(error, probePath)
          resolve(false)
        })
    })
  }

  const reinitialize = async (context = _lastInitContext) => {
    if (_reinitializePromise) {
      return _reinitializePromise
    }

    if (!context?.user || !context?.business) {
      logger.info('firestore reinitialize skipped: missing init context')
      return false
    }

    _reinitializePromise = (async () => {
      isReinitializing.value = true
      logger.info('reinitializing firestore...')

      await destroy()
      await init(context)

      return isInitialized.value
    })().finally(() => {
      isReinitializing.value = false
      _reinitializePromise = undefined
    })

    return _reinitializePromise
  }

  const _defaultErrorHandler = (error, path) => {
    if (isFirestoreShuttingDownMessage(error)) {
      logger.warn('Firestore shutting down')
      return
    }

    logger.error(`error on ${path}:`, error)
  }

  const _getFirestoreData = (type, path, options = {}) => {
    const listenRetryNonce = options.listenRetryNonce

    const { data, changes } = useFirestore(computed(() => {
      if (listenRetryNonce !== undefined) {
        void listenRetryNonce.value
      }

      if (!isInitialized.value) return

      const params = toValue(options?.params)
      if (hasNullOrUndefined(params)) return

      return query(type(FIRESTORE_DB,
        createPath(path, params)
      ), ...toValue((options?.query || [])))
    }), options?.initialValue, {
      autoDispose: false,
      errorHandler: options.onError,
    })

    return {
      data: useDebounce(data, 200),
      changes: useDebounce(changes, 200),
    }
  }

  const _addErrorHandler = (path, options, hasFailed, listenRetry) => {
    return {
      ...options,
      listenRetryNonce: listenRetry?.listenRetryNonce,
      onError: (error) => {
        is(Function, options?.onError) && options.onError(error)

        const markUnready = options.setUnreadyOnError

        if (isFirestoreShuttingDownMessage(error)) {
          if (markUnready) hasFailed.value = true
          _defaultErrorHandler(error, path)
          return
        }

        if (markUnready) hasFailed.value = true

        const canRestart = listenRetry && isRetryableFirestoreListenError(error)

        if (canRestart && listenRetry.totalAutoRestarts.value < MAX_LISTEN_AUTO_RESTARTS) {
          listenRetry.totalAutoRestarts.value++
          listenRetry.scheduleRestart(() => {
            if (markUnready) hasFailed.value = false
            listenRetry.listenRetryNonce.value++
          })
          _defaultErrorHandler(error, path)
          return
        }

        _defaultErrorHandler(error, path)
      },
    }
  }

  const getCollection = (collectionPath, options = {}) => {
    const transform = is(Function, options.transform) ? options.transform : (c) => c
    const hasFailed = shallowRef(false)
    const listenRetry = createListenRetryState()
    const _options = _addErrorHandler(collectionPath, options, hasFailed, listenRetry)

    watch(isInitialized, (_isInitialized) => {
      if (!_isInitialized) {
        listenRetry.clearScheduledRestart()
        return
      }
      hasFailed.value = false
    })

    const { data: collection, changes } = _getFirestoreData(firestoreCollection, collectionPath, _options)

    return {
      collection: computed(() => transform(collection.value?.map((i) => ({ ...i, id: i.id })))),
      changes: computed(() => changes.value),
      isReady: computed(() => isInitialized.value && !isNil(collection.value) && !toValue(hasFailed)),
    }
  }

  const getDocument = (documentPath, options = {}) => {
    const transform = is(Function, options.transform) ? options.transform : (d) => d
    const hasFailed = shallowRef(false)
    const listenRetry = createListenRetryState()
    const _options = _addErrorHandler(documentPath, options, hasFailed, listenRetry)

    watch(isInitialized, (_isInitialized) => {
      if (!_isInitialized) {
        listenRetry.clearScheduledRestart()
        return
      }
      hasFailed.value = false
    })

    const { data: doc } = _getFirestoreData(firestoreDocument, documentPath, _options)

    return {
      doc: computed(() => transform((doc.value && { ...doc.value, id: doc.value?.id }))),
      isReady: computed(() => isInitialized.value && !isNil(doc.value) && !toValue(hasFailed)),
    }
  }

  return {
    init,
    destroy,
    isInitialized: computed(() => isInitialized.value),
    isReinitializing: computed(() => isReinitializing.value),
    probeConnection,
    reinitialize,
    getCollection,
    getDocument,
  }
}
